import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  Grid,
  Paper,
  withStyles,
  Box,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import dialCodes from "../../config/dialCodes";
import axios from "axios";
import { URL } from "../../config/config";
import errorLogo from "../../static/error.png";

const currencies = ["INR", "USD"];

const partnershipTypes = ["Whitelabel", "Direct"];

const existingCustomerBaseOptions = [
  "0-10",
  "11-50",
  "51-100",
  "101-200",
  "201-500",
  "501-1000",
  "1001-2000",
  "2000+",
];

const teamSizeOptions = ["0-10", "11-50", "50-100", "100-200", "200+"];

const partnerEcosystemOptions = [
  "Voice Calling Platform",
  "CRM and Chatbot Platform",
  "Communication Platform",
  "SMS Provider",
  "Digital Marketing Agency",
  "Ecommerce Software",
  "Software Provider",
  "Website & Application Development",
];

const industryOptions = [
  "Fintech",
  "Retail",
  "SaaS",
  "Telecommunication",
  "Mobile Messaging Services",
  "Shopping",
  "Healthcare",
  "Ecommerce Software Companies",
  "Tourism/Hospitality",
  "Pharma and Healthcare",
  "Automobile",
  "Education",
  "Government Entities",
  "Banking",
  "Real Estate",
  "Media & Advertising",
  "All Industry",
];

const metaAdObjectives = [
  "Website Traffic",
  "App Downloads",
  "Sales/Conversions",
  "Lead Generation",
];

const adsBudgetOptions = [
  "Less than 1 Lakh",
  "1–5 Lakh",
  "5–10 Lakh",
  "10–25 Lakh",
  "25–50 Lakh",
];

const initialState = {
  business: {
    partnerBusinessName: "",
    partnerBrandName: "",
    preferredCurrency: "",
    partnershipType: "",
    city: "",
    state: "",
    country: "",
    website: "",
    about: "",
    existingCustomerBase: "",
    teamSize: "",
    partnerEcosystem: [],
    partnerEcosystemOther: "",
    industryFocus: [],
    industryFocusOther: "",
  },
  billing: {
    billingName: "",
    billingAddress: "",
    gstNumber: "",
    billingEmail: "",
    billingContactCountryCode: "",
    billingContactNumber: "",
  },
  signatory: {
    name: "",
    email: "",
    designation: "",
  },
  pocs: [
    { name: "", countryCode: "", number: "", email: "", designation: "" },
    { name: "", countryCode: "", number: "", email: "", designation: "" },
  ],
  ads: {
    runsMetaAds: false,
    metaAdObjective: "",
    overallBudget: "",
    runsClickToWhatsapp: false,
    whatsappAdsBudget: "",
  },
};

const steps = [
  "Business Information",
  "Billing Details",
  "Agreement Signatory",
  "POCs",
  "Ads Information",
  // "Review",
];

const PartnerForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(true);
  const [invalidFormLink, setInvalidFormLink] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitStatus, setSubmitStatus] = useState("idle"); // idle | success | failure

  // Helper function to check if string is only spaces or empty
  const isOnlySpaces = (str) => !str || str.trim() === "";

  // Helper function to validate phone number (only digits)
  const isValidPhoneNumber = (str) => !str || /^\d+$/.test(str);

  const validateStep = (stepIndex) => {
    const newErrors = {};
    if (stepIndex === 0) {
      const b = form.business;
      if (isOnlySpaces(b.partnerBusinessName))
        newErrors.partnerBusinessName = "Required";
      if (isOnlySpaces(b.partnerBrandName))
        newErrors.partnerBrandName = "Required";
      if (!b.preferredCurrency) newErrors.preferredCurrency = "Required";
      if (!b.partnershipType) newErrors.partnershipType = "Required";
      if (isOnlySpaces(b.city)) newErrors.city = "Required";
      if (isOnlySpaces(b.country)) newErrors.country = "Required";
      if (b.website && !/^https?:\/\//i.test(b.website))
        newErrors.website = "Include http(s)://";
      if (!b.partnerEcosystem || b.partnerEcosystem.length === 0)
        newErrors.partnerEcosystemRequired = "Select at least one";
      if (!b.industryFocus || b.industryFocus.length === 0)
        newErrors.industryFocusRequired = "Select at least one";
      if (!b.existingCustomerBase) newErrors.existingCustomerBase = "Required";
      if (!b.teamSize) newErrors.teamSize = "Required";
      if (
        b.partnerEcosystem.includes("Other") &&
        isOnlySpaces(b.partnerEcosystemOther)
      )
        newErrors.partnerEcosystemOther = "Required";
      if (
        b.industryFocus.includes("Other") &&
        isOnlySpaces(b.industryFocusOther)
      )
        newErrors.industryFocusOther = "Required";
    } else if (stepIndex === 1) {
      const bl = form.billing;
      if (isOnlySpaces(bl.billingName)) newErrors.billingName = "Required";
      if (isOnlySpaces(bl.billingAddress))
        newErrors.billingAddress = "Required";
      if (
        bl.billingEmail &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bl.billingEmail)
      )
        newErrors.billingEmail = "Invalid";
      if (isOnlySpaces(bl.billingEmail)) newErrors.billingEmail = "Required";
      if (!bl.billingContactCountryCode)
        newErrors.billingContactCountryCode = "Required";
      if (isOnlySpaces(bl.billingContactNumber))
        newErrors.billingContactNumber = "Required";
      if (
        bl.billingContactNumber &&
        !isValidPhoneNumber(bl.billingContactNumber)
      )
        newErrors.billingContactNumber = "Only numbers allowed";
    } else if (stepIndex === 2) {
      const s = form.signatory;
      if (isOnlySpaces(s.name)) newErrors.signatoryName = "Required";
      if (isOnlySpaces(s.email)) newErrors.signatoryEmail = "Required";
      if (s.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email))
        newErrors.signatoryEmail = "Invalid";
      if (isOnlySpaces(s.designation))
        newErrors.signatoryDesignation = "Required";
    } else if (stepIndex === 3) {
      let completeCount = 0;
      form.pocs.forEach((poc, idx) => {
        const prefix = `poc_${idx}_`;
        const hasAllFields =
          !isOnlySpaces(poc.name) &&
          !!poc.countryCode &&
          !isOnlySpaces(poc.number) &&
          !isOnlySpaces(poc.email) &&
          !isOnlySpaces(poc.designation);
        if (isOnlySpaces(poc.name)) newErrors[prefix + "name"] = "Required";
        if (!poc.countryCode) newErrors[prefix + "countryCode"] = "Required";
        if (isOnlySpaces(poc.number)) newErrors[prefix + "number"] = "Required";
        if (poc.number && !isValidPhoneNumber(poc.number))
          newErrors[prefix + "number"] = "Only numbers allowed";
        if (isOnlySpaces(poc.email)) newErrors[prefix + "email"] = "Required";
        if (poc.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(poc.email))
          newErrors[prefix + "email"] = "Invalid";
        if (isOnlySpaces(poc.designation))
          newErrors[prefix + "designation"] = "Required";
        if (hasAllFields && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(poc.email)) {
          completeCount += 1;
        }
      });
      if (completeCount < 2) {
        newErrors.pocAtLeastTwo = "Add at least two complete POCs";
      }
    } else if (stepIndex === 4) {
      const a = form.ads;
      if (a.runsMetaAds === "yes" && !a.metaAdObjective)
        newErrors.metaAdObjective = "Required";
      if (a.runsMetaAds === "yes" && !a.overallBudget)
        newErrors.overallBudget = "Required";
      if (a.runsClickToWhatsapp === "yes" && !a.whatsappAdsBudget)
        newErrors.whatsappAdsBudget = "Required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const updateField = (path, value) => {
    setForm((prev) => {
      const cloned = JSON.parse(JSON.stringify(prev));
      const segments = path.split(".");
      let obj = cloned;
      for (let i = 0; i < segments.length - 1; i++) {
        obj = obj[segments[i]];
      }
      obj[segments[segments.length - 1]] = value;
      return cloned;
    });
  };

  // Helper function to handle text input with space validation
  const handleTextInput = (path, value) => {
    // Don't allow input that starts with spaces or contains only spaces
    if (value && value.trim() === "" && value.length > 0) {
      return; // Don't update if trying to enter only spaces
    }
    updateField(path, value);
  };

  // Helper function to handle phone number input (only digits)
  const handlePhoneInput = (path, value) => {
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, "");
    updateField(path, digitsOnly);
  };

  const toggleFromArray = (path, option) => {
    setForm((prev) => {
      const cloned = JSON.parse(JSON.stringify(prev));
      const segments = path.split(".");
      let arr = cloned;
      for (let i = 0; i < segments.length; i++) {
        arr = arr[segments[i]];
      }
      const idx = arr.indexOf(option);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(option);
      return cloned;
    });
  };

  const addPoc = () => {
    if (form.pocs.length >= 5) return;
    setForm((prev) => ({
      ...prev,
      pocs: [
        ...prev.pocs,
        { name: "", countryCode: "", number: "", email: "", designation: "" },
      ],
    }));
  };
  const removePoc = (idxToRemove) => {
    setForm((prev) => ({
      ...prev,
      pocs: prev.pocs.filter((_, idx) => idx !== idxToRemove),
    }));
    setErrors((prev) => {
      const clone = { ...prev };
      delete clone.pocAddBlocked;
      return clone;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (let i = 0; i < 5; i++) {
      if (!validateStep(i)) {
        setActiveStep(i);
        return;
      }
    }
    try {
      setSubmitLoading(true);
      const searchParams = new URLSearchParams(window.location.search);
      const encrypted_query_string = searchParams.get("encrypted_query_string");

      const { data } = await axios.post(`${URL}/form/submit-form`, {
        encrypted_query_string,
        updateObj: form,
      });
      setSubmitStatus("success");
    } catch (e) {
      console.error(e);
      setSubmitError(
        e?.response?.data?.message ||
          e?.response?.data?.errorMessage ||
          "Something went wrong while submiting the form"
      );
      setSubmitStatus("failure");
    }
    setSubmitLoading(false);
  };

  const renderBusiness = () => {
    const b = form.business;
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Partner Business Name *
          </Typography>
          <TextField
            fullWidth
            placeholder="Partner Business Name"
            value={b.partnerBusinessName}
            onChange={(e) =>
              handleTextInput("business.partnerBusinessName", e.target.value)
            }
            error={!!errors.partnerBusinessName}
            helperText={errors.partnerBusinessName}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Partner Brand Name *
          </Typography>
          <TextField
            fullWidth
            placeholder="Partner Brand Name"
            value={b.partnerBrandName}
            onChange={(e) =>
              handleTextInput("business.partnerBrandName", e.target.value)
            }
            error={!!errors.partnerBrandName}
            helperText={errors.partnerBrandName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Preferred Currency *
          </Typography>
          <Autocomplete
            options={currencies}
            value={b.preferredCurrency || null}
            onChange={(e, val) =>
              updateField("business.preferredCurrency", val || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Preferred Currency"
                variant="outlined"
                error={!!errors.preferredCurrency}
                helperText={errors.preferredCurrency}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Partnership Type *
          </Typography>
          <Autocomplete
            options={partnershipTypes}
            value={b.partnershipType || null}
            onChange={(e, val) =>
              updateField("business.partnershipType", val || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Partnership Type"
                variant="outlined"
                error={!!errors.partnershipType}
                helperText={errors.partnershipType}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            City *
          </Typography>
          <TextField
            fullWidth
            placeholder="City"
            value={b.city}
            onChange={(e) => handleTextInput("business.city", e.target.value)}
            error={!!errors.city}
            helperText={errors.city}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            State (optional)
          </Typography>
          <TextField
            fullWidth
            placeholder="State (optional)"
            value={b.state}
            onChange={(e) => handleTextInput("business.state", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Country *
          </Typography>
          <TextField
            fullWidth
            placeholder="Country"
            value={b.country}
            onChange={(e) =>
              handleTextInput("business.country", e.target.value)
            }
            error={!!errors.country}
            helperText={errors.country}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Website (include http(s)://)
          </Typography>
          <TextField
            fullWidth
            placeholder="Website (include http(s)://)"
            value={b.website}
            onChange={(e) =>
              handleTextInput("business.website", e.target.value)
            }
            error={!!errors.website}
            helperText={errors.website}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            About your Business
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="About your Business"
            value={b.about}
            onChange={(e) => handleTextInput("business.about", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Existing Customer Base *
          </Typography>
          <Autocomplete
            options={existingCustomerBaseOptions}
            value={b.existingCustomerBase || null}
            onChange={(e, val) =>
              updateField("business.existingCustomerBase", val || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Existing Customer Base"
                variant="outlined"
                error={!!errors.existingCustomerBase}
                helperText={errors.existingCustomerBase}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Team Size *
          </Typography>
          <Autocomplete
            options={teamSizeOptions}
            value={b.teamSize || null}
            onChange={(e, val) => updateField("business.teamSize", val || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Team Size"
                variant="outlined"
                error={!!errors.teamSize}
                helperText={errors.teamSize}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1">
            Partner Ecosystem (Multi-select)
          </Typography>
          <FormGroup row>
            {partnerEcosystemOptions.map((opt) => (
              <FormControlLabel
                key={opt}
                control={
                  <Checkbox
                    checked={b.partnerEcosystem.includes(opt)}
                    onChange={() =>
                      toggleFromArray("business.partnerEcosystem", opt)
                    }
                  />
                }
                label={opt}
              />
            ))}
            <FormControlLabel
              control={
                <Checkbox
                  checked={b.partnerEcosystem.includes("Other")}
                  onChange={() =>
                    toggleFromArray("business.partnerEcosystem", "Other")
                  }
                />
              }
              label="Other"
            />
          </FormGroup>
          {errors.partnerEcosystemRequired && (
            <Typography variant="caption" color="error">
              {errors.partnerEcosystemRequired}
            </Typography>
          )}
          {b.partnerEcosystem.includes("Other") && (
            <>
              <Typography
                variant="subtitle2"
                style={{ marginBottom: 8, marginTop: 16 }}
              >
                Please specify *
              </Typography>
              <TextField
                fullWidth
                placeholder="Please specify"
                value={b.partnerEcosystemOther}
                onChange={(e) =>
                  handleTextInput(
                    "business.partnerEcosystemOther",
                    e.target.value
                  )
                }
                error={!!errors.partnerEcosystemOther}
                helperText={errors.partnerEcosystemOther}
              />
            </>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1">
            Industry Focus (Multi-select)
          </Typography>
          <FormGroup row>
            {industryOptions.map((opt) => (
              <FormControlLabel
                key={opt}
                control={
                  <Checkbox
                    checked={b.industryFocus.includes(opt)}
                    onChange={() =>
                      toggleFromArray("business.industryFocus", opt)
                    }
                  />
                }
                label={opt}
              />
            ))}
            <FormControlLabel
              control={
                <Checkbox
                  checked={b.industryFocus.includes("Other")}
                  onChange={() =>
                    toggleFromArray("business.industryFocus", "Other")
                  }
                />
              }
              label="Other"
            />
          </FormGroup>
          {errors.industryFocusRequired && (
            <Typography variant="caption" color="error">
              {errors.industryFocusRequired}
            </Typography>
          )}
          {b.industryFocus.includes("Other") && (
            <>
              <Typography
                variant="subtitle2"
                style={{ marginBottom: 8, marginTop: 16 }}
              >
                Please specify *
              </Typography>
              <TextField
                fullWidth
                placeholder="Please specify"
                value={b.industryFocusOther}
                onChange={(e) =>
                  handleTextInput("business.industryFocusOther", e.target.value)
                }
                error={!!errors.industryFocusOther}
                helperText={errors.industryFocusOther}
              />
            </>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderBilling = () => {
    const bl = form.billing;
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Billing Name *
          </Typography>
          <TextField
            fullWidth
            placeholder="Billing Name"
            value={bl.billingName}
            onChange={(e) =>
              handleTextInput("billing.billingName", e.target.value)
            }
            error={!!errors.billingName}
            helperText={errors.billingName}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Billing Address *
          </Typography>
          <TextField
            fullWidth
            placeholder="Billing Address"
            value={bl.billingAddress}
            onChange={(e) =>
              handleTextInput("billing.billingAddress", e.target.value)
            }
            error={!!errors.billingAddress}
            helperText={errors.billingAddress}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            GST number (if available)
          </Typography>
          <TextField
            fullWidth
            placeholder="GST number (if available)"
            value={bl.gstNumber}
            onChange={(e) =>
              handleTextInput("billing.gstNumber", e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Billing Email ID *
          </Typography>
          <TextField
            fullWidth
            placeholder="Billing Email ID"
            value={bl.billingEmail}
            onChange={(e) =>
              handleTextInput("billing.billingEmail", e.target.value)
            }
            error={!!errors.billingEmail}
            helperText={errors.billingEmail}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Billing Contact Number *
          </Typography>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={6} sm={5}>
              <Autocomplete
                options={dialCodes}
                getOptionLabel={(opt) => `${opt.dialCode} ${opt.name}`}
                value={
                  dialCodes.find(
                    (d) => d.dialCode === bl.billingContactCountryCode
                  ) || null
                }
                onChange={(e, val) =>
                  updateField(
                    "billing.billingContactCountryCode",
                    val ? val.dialCode : ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Country Code"
                    InputProps={{
                      ...params.InputProps,
                      style: { height: 48 },
                    }}
                    inputProps={{
                      ...params.inputProps,
                      style: { padding: "12px" },
                    }}
                    error={!!errors.billingContactCountryCode}
                    helperText={errors.billingContactCountryCode}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={7}>
              <TextField
                fullWidth
                placeholder="Billing Contact Number"
                value={bl.billingContactNumber}
                onChange={(e) =>
                  handlePhoneInput(
                    "billing.billingContactNumber",
                    e.target.value
                  )
                }
                InputProps={{ style: { height: 48 } }}
                inputProps={{ style: { padding: "12px" } }}
                error={!!errors.billingContactNumber}
                helperText={errors.billingContactNumber}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderSignatory = () => {
    const s = form.signatory;
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Signatory Name *
          </Typography>
          <TextField
            fullWidth
            placeholder="Signatory Name"
            value={s.name}
            onChange={(e) => handleTextInput("signatory.name", e.target.value)}
            error={!!errors.signatoryName}
            helperText={errors.signatoryName}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Signatory Email *
          </Typography>
          <TextField
            fullWidth
            placeholder="Signatory Email"
            value={s.email}
            onChange={(e) => handleTextInput("signatory.email", e.target.value)}
            error={!!errors.signatoryEmail}
            helperText={errors.signatoryEmail}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Signatory Designation *
          </Typography>
          <TextField
            fullWidth
            placeholder="Signatory Designation"
            value={s.designation}
            onChange={(e) =>
              handleTextInput("signatory.designation", e.target.value)
            }
            error={!!errors.signatoryDesignation}
            helperText={errors.signatoryDesignation}
          />
        </Grid>
      </Grid>
    );
  };

  const renderPOCs = () => {
    return (
      <Grid container spacing={2}>
        {form.pocs.map((poc, idx) => (
          <Grid key={idx} item xs={12}>
            <Paper style={{ padding: 16 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                    POC {idx + 1} Name *
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder={`POC ${idx + 1} Name`}
                    value={poc.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Don't allow input that starts with spaces or contains only spaces
                      if (value && value.trim() === "" && value.length > 0) {
                        return; // Don't update if trying to enter only spaces
                      }
                      setForm((prev) => {
                        const pocs = prev.pocs.slice();
                        pocs[idx] = { ...pocs[idx], name: value };
                        return { ...prev, pocs };
                      });
                    }}
                    error={!!errors[`poc_${idx}_name`]}
                    helperText={errors[`poc_${idx}_name`]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                    Email *
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Email"
                    value={poc.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Don't allow input that starts with spaces or contains only spaces
                      if (value && value.trim() === "" && value.length > 0) {
                        return; // Don't update if trying to enter only spaces
                      }
                      setForm((prev) => {
                        const pocs = prev.pocs.slice();
                        pocs[idx] = { ...pocs[idx], email: value };
                        return { ...prev, pocs };
                      });
                    }}
                    error={!!errors[`poc_${idx}_email`]}
                    helperText={errors[`poc_${idx}_email`]}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                    Designation *
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Designation"
                    value={poc.designation}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Don't allow input that starts with spaces or contains only spaces
                      if (value && value.trim() === "" && value.length > 0) {
                        return; // Don't update if trying to enter only spaces
                      }
                      setForm((prev) => {
                        const pocs = prev.pocs.slice();
                        pocs[idx] = { ...pocs[idx], designation: value };
                        return { ...prev, pocs };
                      });
                    }}
                    InputProps={{ style: { height: 48 } }}
                    inputProps={{ style: { padding: "12px" } }}
                    error={!!errors[`poc_${idx}_designation`]}
                    helperText={errors[`poc_${idx}_designation`]}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                    Contact Number *
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={4}>
                      <Autocomplete
                        options={dialCodes}
                        getOptionLabel={(opt) => `${opt.dialCode} ${opt.name}`}
                        value={
                          dialCodes.find(
                            (d) => d.dialCode === poc.countryCode
                          ) || null
                        }
                        onChange={(e, val) => {
                          setForm((prev) => {
                            const pocs = prev.pocs.slice();
                            pocs[idx] = {
                              ...pocs[idx],
                              countryCode: val ? val.dialCode : "",
                            };
                            return { ...prev, pocs };
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Code"
                            InputProps={{
                              ...params.InputProps,
                              style: { height: 48 },
                            }}
                            inputProps={{
                              ...params.inputProps,
                              style: { padding: "12px" },
                            }}
                            error={!!errors[`poc_${idx}_countryCode`]}
                            helperText={errors[`poc_${idx}_countryCode`]}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        placeholder="Number"
                        value={poc.number}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow digits
                          const digitsOnly = value.replace(/\D/g, "");
                          setForm((prev) => {
                            const pocs = prev.pocs.slice();
                            pocs[idx] = { ...pocs[idx], number: digitsOnly };
                            return { ...prev, pocs };
                          });
                        }}
                        InputProps={{ style: { height: 48 } }}
                        inputProps={{ style: { padding: "12px" } }}
                        error={!!errors[`poc_${idx}_number`]}
                        helperText={errors[`poc_${idx}_number`]}
                      />
                    </Grid>
                    <Grid item xs={2}></Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={1}>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "flex-end",
                      height: "100%",
                    }}
                  >
                    <Button
                      onClick={() => removePoc(idx)}
                      disabled={form.pocs.length === 1}
                    >
                      Remove
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={addPoc}
            disabled={form.pocs.length >= 10}
            style={{
              opacity: form.pocs.length >= 10 ? 0.4 : 1,
              cursor: form.pocs.length >= 10 ? "not-allowed" : "pointer",
              backgroundColor: form.pocs.length >= 10 ? "#e0e0e0" : undefined,
              color: form.pocs.length >= 10 ? "#9e9e9e" : undefined,
            }}
          >
            Add POC
          </Button>
        </Grid>
        {errors.pocAtLeastTwo && (
          <Grid item xs={12}>
            <Typography variant="caption" color="error">
              {errors.pocAtLeastTwo}
            </Typography>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderAds = () => {
    const a = form.ads;
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            Do you run Meta Ads for your customers?
          </Typography>
          <RadioGroup
            row
            value={a.runsMetaAds}
            onChange={(e) =>
              updateField("ads.runsMetaAds", e.target.value === "true")
            }
          >
            <FormControlLabel value={true} control={<Radio />} label="Yes" />
            <FormControlLabel value={false} control={<Radio />} label="No" />
          </RadioGroup>
        </Grid>
        {a.runsMetaAds && (
          <>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Objective of Ads *
              </Typography>
              <Autocomplete
                options={metaAdObjectives}
                value={a.metaAdObjective || null}
                onChange={(e, val) =>
                  updateField("ads.metaAdObjective", val || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Objective of Ads"
                    variant="outlined"
                    error={!!errors.metaAdObjective}
                    helperText={errors.metaAdObjective}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Overall Ads Budget *
              </Typography>
              <Autocomplete
                options={adsBudgetOptions}
                value={a.overallBudget || null}
                onChange={(e, val) =>
                  updateField("ads.overallBudget", val || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Overall Ads Budget"
                    variant="outlined"
                    error={!!errors.overallBudget}
                    helperText={errors.overallBudget}
                  />
                )}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Typography variant="subtitle1">
            Do you run Click-to-WhatsApp Ads?
          </Typography>
          <RadioGroup
            row
            value={a.runsClickToWhatsapp}
            onChange={(e) =>
              updateField("ads.runsClickToWhatsapp", e.target.value === "true")
            }
          >
            <FormControlLabel value={true} control={<Radio />} label="Yes" />
            <FormControlLabel value={false} control={<Radio />} label="No" />
          </RadioGroup>
        </Grid>
        {a.runsClickToWhatsapp && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              WhatsApp Ads Budget *
            </Typography>
            <Autocomplete
              options={adsBudgetOptions}
              value={a.whatsappAdsBudget || null}
              onChange={(e, val) =>
                updateField("ads.whatsappAdsBudget", val || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="WhatsApp Ads Budget"
                  variant="outlined"
                  error={!!errors.whatsappAdsBudget}
                  helperText={errors.whatsappAdsBudget}
                />
              )}
            />
          </Grid>
        )}

        {submitError && (
          <Grid item xs={12}>
            {submitError}
          </Grid>
        )}
      </Grid>
    );
  };

  const renderReview = () => {
    return (
      <pre
        style={{ whiteSpace: "pre-wrap", background: "#f7f7f7", padding: 16 }}
      >
        {JSON.stringify(form, null, 2)}
      </pre>
    );
  };

  const body = useMemo(() => {
    switch (activeStep) {
      case 0:
        return renderBusiness();
      case 1:
        return renderBilling();
      case 2:
        return renderSignatory();
      case 3:
        return renderPOCs();
      case 4:
        return renderAds();
      case 5:
        return renderReview();
      default:
        return null;
    }
  }, [activeStep, form, errors]);

  const checkFormValidity = async (encrypted_query_string) => {
    if (encrypted_query_string) {
      try {
        const { data } = await axios.post(`${URL}/form/validate-link`, {
          encrypted_query_string,
        });
        if (data.valid) {
          setFormLoading(false);
        } else {
          setFormLoading(false);
          setInvalidFormLink(true);
          setFormError("Form link is invalid");
        }
      } catch (e) {
        setFormLoading(false);
        setInvalidFormLink(true);
        setFormError(e?.response?.data?.message || "Form link is invalid");
      }
    } else {
      setFormLoading(false);
      setInvalidFormLink(true);
      setFormError("Form link is invalid");
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const encrypted_query_string = searchParams.get("encrypted_query_string");

    checkFormValidity(encrypted_query_string);
  }, []);

  if (formLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (invalidFormLink) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <img src={errorLogo} alt="Error" />
        <Typography variant="h3">{formError}</Typography>
      </div>
    );
  }

  if (submitStatus === "success") {
    return (
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px" }}>
        <Typography variant="h5" style={{ marginBottom: 12 }}>
          Submission Successful
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 16 }}>
          Thank you. Your details have been submitted successfully.
        </Typography>
      </div>
    );
  }

  if (submitStatus === "failure") {
    return (
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px" }}>
        <Typography variant="h5" style={{ marginBottom: 12 }}>
          Submission Failed
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 16 }}>
          Something went wrong while submitting your details. Please try again.
        </Typography>
        <Button
          color="primary"
          variant="contained"
          onClick={() => setSubmitStatus("idle")}
        >
          Back to Form
        </Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "0px 24px 88px" }}>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 10,
          background: "white",
          paddingTop: "16px",
          width: "100%",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px" }}>
          <Typography variant="h3" style={{ marginBottom: 8 }}>
            Partner Onboarding Form
          </Typography>
          {/* Show full stepper on larger screens */}
          <div style={{ display: "none" }} className="stepper-desktop">
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => {
                const isCompleted = index < activeStep;
                const clickableProps = isCompleted
                  ? {
                      onClick: () => setActiveStep(index),
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ")
                          setActiveStep(index);
                      },
                      role: "button",
                      tabIndex: 0,
                      style: { cursor: "pointer" },
                    }
                  : {};
                return (
                  <Step key={label} {...clickableProps}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </div>
          {/* Show simple header on smaller screens */}
          <div style={{ display: "block" }} className="stepper-mobile">
            <Typography variant="h4" style={{ padding: "16px 0" }}>
              Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
            </Typography>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-content">{body}</div>
      </form>

      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          background: "#fff",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
          padding: "12px 0",
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            padding: "0 24px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>
          {activeStep < steps.length - 1 ? (
            <Button
              color="primary"
              variant="contained"
              onClick={handleNext}
              disabled={submitLoading}
            >
              Next
            </Button>
          ) : (
            <Button
              color="primary"
              variant="contained"
              type="submit"
              onClick={handleSubmit}
              disabled={submitLoading}
            >
              {submitLoading ? (
                <>
                  <CircularProgress
                    size={18}
                    style={{ color: "#fff", marginRight: 8 }}
                  />
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
const styles = (theme) => ({
  "@global": {
    ".MuiStepIcon-text": {
      fill: "#fff",
    },
    ".stepper-desktop": {
      [theme.breakpoints.up("sm")]: {
        display: "block !important",
      },
    },
    ".stepper-mobile": {
      [theme.breakpoints.up("sm")]: {
        display: "none !important",
      },
    },
    ".form-content": {
      marginTop: "120px",
      [theme.breakpoints.up("sm")]: {
        marginTop: "168px",
      },
    },
  },
});

export default withStyles(styles)(PartnerForm);
